/**
 * Utility functions for Ottawa location-based features
 */

// Ottawa coordinates (Parliament Hill)
const OTTAWA_LAT = 45.4215;
const OTTAWA_LNG = -75.6972;

// 50km radius in meters
const OTTAWA_RADIUS_METERS = 50000;

/**
 * Calculate distance between two coordinates using Haversine formula
 * @param {number} lat1 - First latitude
 * @param {number} lng1 - First longitude
 * @param {number} lat2 - Second latitude
 * @param {number} lng2 - Second longitude
 * @returns {number} Distance in meters
 */
export const calculateDistance = (lat1, lng1, lat2, lng2) => {
    const R = 6371000; // Earth's radius in meters
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
        Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
};

/**
 * Check if user is within 50km of Ottawa
 * @param {Object} locationInfo - Location info from user.duck.js detectedCurrentLocation
 * @returns {boolean} True if within 50km of Ottawa
 */
export const isWithinOttawaRadius = (locationInfo) => {
    if (!locationInfo?.origin?.lat || !locationInfo?.origin?.lng) {
        return false;
    }

    const distance = calculateDistance(
        OTTAWA_LAT,
        OTTAWA_LNG,
        locationInfo.origin.lat,
        locationInfo.origin.lng
    );

    return distance <= OTTAWA_RADIUS_METERS;
};

/**
 * Get the appropriate phone number based on user location
 * @param {Object} locationInfo - Location info from user.duck.js detectedCurrentLocation
 * @param {string} defaultPhoneNumber - Default phone number from env
 * @returns {string} Phone number to display
 */
export const getLocationBasedPhoneNumber = (locationInfo, defaultPhoneNumber = '1-833-234-RENT') => {
    const ottawaPhoneNumber = '1-613-501-5184';

    if (isWithinOttawaRadius(locationInfo)) {
        return ottawaPhoneNumber;
    }

    return defaultPhoneNumber;
};
